<template>
  <v-stepper class="fill-height" v-model="step">
    <v-stepper-header>
      <v-stepper-step :complete="step > 1" step="1"> 1. Wochen-Vorlage </v-stepper-step>
      <v-stepper-step :complete="step > 2" step="2"> 2. Spezialtage </v-stepper-step>
      <v-stepper-step :complete="step > 3" step="3"> 3. Mitarbeiter-Einstellungen </v-stepper-step>
      <v-stepper-step :complete="step > 4" step="4"> 4. Automatische Zuweisung </v-stepper-step>
      <v-stepper-step step="5"> 5. Einsatzplan-Übersicht </v-stepper-step>
    </v-stepper-header>

    <v-stepper-items class="fill-height" style="overflow: auto; padding-bottom: 250px">
      <v-stepper-content step="1">
        <v-container fluid>
          <v-row v-if="availableStudios.length > 1" class="mb-4 px-4">
            <v-col cols="12" md="4">
              <v-select
                filled
                dense
                hide-details
                :items="availableStudios"
                label="Studio für Vorlage wählen"
                v-model="selectedStudio"
                item-text="name"
                item-value="_id"
                @change="handleStudioChange"></v-select>
            </v-col>
          </v-row>
          <v-row v-else-if="availableStudios.length === 1 && !selectedStudio">
            {{ autoSelectStudio() }}
          </v-row>

          <weekly-template-planner
            v-if="!loading && selectedStudio && settings.shifts && settings.shifts.length > 0"
            :studio-id="selectedStudio"
            :available-shifts="settings.shifts"
            :key="`weekly-planner-${selectedStudio}`"
            @template-saved="handleTemplateUpdate" />
          <v-container v-else-if="loading" class="text-center fill-height d-flex justify-center align-center flex-column">
            <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
            <p class="mt-4 grey--text">Lade initiale Daten...</p>
          </v-container>
          <v-container v-else class="text-center mt-5">
            <p v-if="!selectedStudio && availableStudios.length > 0">Bitte wählen Sie ein Studio aus.</p>
            <p v-else-if="availableStudios.length === 0">Keine Studios zum Planen verfügbar.</p>
            <p v-else-if="!settings.shifts || settings.shifts.length === 0">
              Keine Schichtvorlagen gefunden. Bitte definieren Sie zuerst Schichten in den Einstellungen.
            </p>
            <p v-else>Lade Vorlagendaten...</p>
          </v-container>
        </v-container>
      </v-stepper-content>

      <v-stepper-content step="2">
        <v-container fluid>
          <v-card-title>Planungszeitraum</v-card-title>
          <v-row>
            <v-col cols="12" md="4">
              <v-menu v-model="dateMenu" :close-on-content-click="false" transition="scale-transition" offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="dateRangeText"
                    label="Zeitraum auswählen"
                    prepend-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    class="mx-3"></v-text-field>
                </template>
                <v-date-picker v-model="planningDateRange" range no-title scrollable @input="dateMenu = false"></v-date-picker>
              </v-menu>
            </v-col>
          </v-row>
          <v-card-title>Spezialtage definieren</v-card-title>
          <v-card-text
            >(Hier kommt die Kalenderansicht hin, um abweichende Tage (z.B. Feiertage, Events) von der Wochen-Vorlage zu definieren.)</v-card-text
          >
        </v-container>
      </v-stepper-content>

      <v-stepper-content step="3">
        <shift-management-employee-settings
          v-if="step === 3 && selectedStudio && planningDateRange.length === 2"
          :studio-id="selectedStudio"
          :planning-start-date="planningDateRange[0]"
          :planning-end-date="planningDateRange[1]"
          :weekly-template-data="loadedWeeklyTemplate"
          :available-shifts="settings.shifts"
          :school-holidays="settings.schoolHolidays || []"
          ref="employeeSettings" />
        <v-container v-else class="text-center pa-5">
          <p>Bitte wählen Sie in Schritt 1 ein Studio und in Schritt 2 einen gültigen Planungszeitraum aus.</p>
          <v-btn text color="primary" @click="step = 1">Zurück zur Konfiguration</v-btn>
        </v-container>
      </v-stepper-content>

      <v-stepper-content step="4">
        <shift-management-assignment
          v-if="step === 4"
          :studio-id="selectedStudio"
          :planning-start-date="planningDateRange[0]"
          :planning-end-date="planningDateRange[1]"
          @assignment-complete="handleAssignmentComplete"
          @view-plan="step = 5"
          ref="assignmentComponent" />
      </v-stepper-content>

      <v-stepper-content step="5">
        <shift-management-roster-view
          v-if="step === 5 && assignedPlanData.length > 0 && assignedEmployees.length > 0"
          :plan-data="assignedPlanData"
          :employees="assignedEmployees"
          :start-date="planningDateRange[0]"
          :school-holidays="settings.schoolHolidays || []"
          :loading="assigning" />
        <div v-else-if="step === 5" class="text-center pa-5">
          <p v-if="!assigning">
            Es konnten keine Zuweisungsdaten gefunden werden. Bitte führen Sie die automatische Zuweisung in Schritt 4 erneut aus.
          </p>
          <p v-else>Plan wird noch geladen...</p>
          <v-btn color="primary" @click="step = 4">Zurück zu Schritt 4</v-btn>
        </div>
      </v-stepper-content>
    </v-stepper-items>

    <v-card-actions style="position: fixed; bottom: 0; width: 100vw; justify-content: right; background: #121212; z-index: 5">
      <v-btn x-large color="warning" @click="showConfirmationDialog = true" class="mr-auto ml-5" v-if="step === 3">
        <v-icon left>mdi-alert-circle-outline</v-icon>
        Prüfung vor Zuweisung
      </v-btn>

      <v-btn x-large color="success" @click="triggerAssignment" :loading="assigning" class="mr-auto ml-5" v-if="step === 4">
        <v-icon left>mdi-auto-fix</v-icon>
        Zuweisung Starten
      </v-btn>

      <v-btn color="secondary" @click="prevStep" :disabled="step === 1">Zurück</v-btn>
      <v-btn color="primary" @click="nextStep" class="mr-7" :disabled="step === 5">Weiter</v-btn>
    </v-card-actions>

    <v-dialog v-model="showConfirmationDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title class="warning--text text--darken-2">
          <v-icon left color="warning">mdi-alert-circle-outline</v-icon>
          Achtung: Nicht alle Mitarbeiter bereit!
        </v-card-title>
        <v-card-text>
          <span v-if="confirmationDialogData.notReadyNames.length > 0">
            Folgende Mitarbeiter wurden nicht als "Bereit für Planung" markiert und werden bei der automatischen Zuweisung ignoriert:
            <ul>
              <li v-for="name in confirmationDialogData.notReadyNames" :key="name">{{ name }}</li>
            </ul>
          </span>
          <span v-else> Alle Mitarbeiter sind als "Bereit" markiert. </span>
          <br /><br />
          Möchten Sie mit den <strong>{{ confirmationDialogData.readyEmployeeIds.length }}</strong> als "Bereit" markierten Mitarbeitern fortfahren?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showConfirmationDialog = false">Abbrechen</v-btn>
          <v-btn color="warning" text @click="proceedToAssignment">Fortfahren</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-stepper>
</template>
<script>
import api from "@/api"
// Import aller 4 Komponenten
import WeeklyTemplatePlanner from "./WeeklyTemplatePlanner.vue"
import ShiftManagementEmployeeSettings from "./shiftManagementMainDialogCreateEmployee.vue"
import ShiftManagementAssignment from "./shiftManagementAssignment.vue"
import ShiftManagementRosterView from "./shiftManagementRosterView.vue"
import { mapState } from "vuex"

export default {
  name: "ArbeitsplanStepper",
  components: {
    WeeklyTemplatePlanner,
    ShiftManagementEmployeeSettings,
    ShiftManagementAssignment,
    ShiftManagementRosterView,
  },
  data() {
    return {
      step: 1, // Startet bei Schritt 1 (Vorlage)
      loading: true,
      assigning: false,
      planningDateRange: [this.$moment().startOf("month").format("YYYY-MM-DD"), this.$moment().endOf("month").format("YYYY-MM-DD")],
      initialLoadComplete: false,
      dateMenu: false,

      selectedStudio: null,
      availableStudios: [],
      settings: { shifts: [] }, // Hält die verfügbaren Schicht-Vorlagen

      loadedWeeklyTemplate: [], // Speichert die geladene Vorlage aus Schritt 1

      assignedPlanData: [],
      assignedEmployees: [],

      // Für den Bestätigungsdialog in Schritt 3
      showConfirmationDialog: false,
      confirmationDialogData: {
        notReadyNames: [],
        readyEmployeeIds: [],
      },

      // *** NEU: Speicher für die Mitarbeiterdaten aus Schritt 3 ***
      confirmedEmployeeSettings: [],
    }
  },
  async created() {
    this.loading = true
    try {
      // Lade Schicht-Vorlagen und Studios
      ;[this.settings, this.availableStudios] = await Promise.all([
        api.shiftManagement.fetchSettings("local"), // Annahme: Lädt 'shifts'
        api.fetchAllowedAdminStudios(), // Oder api.fetchAllStudios()
      ])

      if (this.availableStudios.length > 0) {
        // Automatisch erstes Studio auswählen, WENN selectedStudio noch null ist
        if (!this.selectedStudio) {
          this.selectedStudio = this.availableStudios[0]._id
        }
        // Lade die Wochen-Vorlage für das vorausgewählte Studio
        await this.fetchWeeklyTemplate()
      }
    } catch (error) {
      this.$toast.error("Fehler beim Initialisieren der Daten.")
      console.error("Initialization Error:", error)
    } finally {
      this.loading = false
      this.initialLoadComplete = true
    }
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),
    dateRangeText() {
      if (!this.planningDateRange || this.planningDateRange.length < 2) return ""
      const [start, end] = [...this.planningDateRange].sort()
      return `${this.$moment(start).format("DD.MM.YY")} - ${this.$moment(end).format("DD.MM.YY")}`
    },
  },
  methods: {
    calculatePaidVacationHours(employeeData, planningSettings) {
      if (!this.planningDateRange || this.planningDateRange.length < 2) return 0

      const start = this.$moment(this.planningDateRange[0])
      const end = this.$moment(this.planningDateRange[1])
      const studioInfo = employeeData.studio.find((s) => s._id === this.selectedStudio)
      if (!studioInfo) return 0

      const percentage = this.getPlanningPercentage(employeeData, studioInfo, planningSettings)
      const dailyHours = 8.4 * (percentage / 100)
      let vacationHours = 0

      // Gehe von den vollen Feriendaten aus, die von Schritt 3 übergeben wurden
      ;(employeeData.vacations || []).forEach((vac) => {
        ;(vac.dates || []).forEach((d) => {
          const dateMoment = this.$moment(d.date)
          // Prüfe, ob der Ferientag im Planungszeitraum liegt
          if (dateMoment.isBetween(start, end, null, "[]")) {
            const dayOfWeekISO = dateMoment.isoWeekday()
            // Zähle nur Wochentage (Mo-Fr)
            if (dayOfWeekISO <= 5) {
              vacationHours += dailyHours
            }
          }
        })
      })
      return vacationHours
    },
    // NEU: Hinzugefügt in 'test/shiftManagementMainDialogCreate.vue' -> methods:
    getPlanningPercentage(employee, studioInfo, planningSettings) {
      if (!studioInfo && !employee.timeTrackerPercentage) return 0

      // Priorität 1: Temporäre Einstellung (übergebenes planningSettings Objekt)
      const planningPercentages = planningSettings?.percentages // Verwende das übergebene Objekt
      if (planningPercentages && planningPercentages[studioInfo._id] != null) {
        return planningPercentages[studioInfo._id]
      }
      // Priorität 2: Globales timeTrackerPercentage (falls gesetzt)
      if (employee.timeTrackerPercentage != null) {
        return employee.timeTrackerPercentage
      }
      // Priorität 3: Studio-spezifisches Pensum
      if (studioInfo) {
        return studioInfo.percentage
      }
      return 0 // Fallback
    },
    // Wird aufgerufen, wenn der Studio-Select geändert wird
    async handleStudioChange(newStudioId) {
      if (!newStudioId || !this.initialLoadComplete) return
      this.loading = true // Ladeanzeige für Vorlagen-Wechsel
      await this.fetchWeeklyTemplate() // Lade die Vorlage für das neue Studio
      this.loading = false
    },

    // Lädt die Wochen-Vorlage für das `selectedStudio`
    async fetchWeeklyTemplate() {
      if (!this.selectedStudio) {
        this.loadedWeeklyTemplate = []
        return
      }
      console.log("Hauptdialog: Lade WeeklyTemplate für Studio " + this.selectedStudio)
      this.loading = true // Setze Ladezustand für Vorlage
      try {
        // Ruft die API-Funktion auf, die wir im Backend erstellt haben
        this.loadedWeeklyTemplate = await api.shiftManagement.fetchWeeklyTemplate(this.selectedStudio)
        console.log("Hauptdialog: Vorlage geladen", this.loadedWeeklyTemplate)
      } catch (error) {
        this.$toast.error("Wochen-Vorlage konnte nicht geladen werden.")
        console.error(error)
        this.loadedWeeklyTemplate = [] // Setze auf leer bei Fehler
      } finally {
        this.loading = false // Beende Ladezustand
      }
    },

    // Wird von WeeklyTemplatePlanner aufgerufen, wenn dort gespeichert wird
    handleTemplateUpdate(savedTemplate) {
      this.loadedWeeklyTemplate = savedTemplate // Aktualisiere die Daten im Parent
      this.$toast.success("Vorlage im Hauptdialog aktualisiert.")
    },

    // *** KORRIGIERT: Greift jetzt auf confirmedEmployeeSettings zu ***
    async triggerAssignment() {
      // 1. Prüfungen (unverändert)
      if (!this.confirmationDialogData.readyEmployeeIds || this.confirmationDialogData.readyEmployeeIds.length === 0) {
        this.$toast.error("Keine Mitarbeiter zur Zuweisung ausgewählt. Bitte zu Schritt 3 zurückkehren.")
        return
      }
      if (!this.confirmedEmployeeSettings || this.confirmedEmployeeSettings.length === 0) {
        this.$toast.error("Fehler: Gespeicherte Mitarbeiterdaten nicht gefunden. Bitte zu Schritt 3 zurückkehren.")
        return
      }

      this.assigning = true // Ladebalken starten
      const planningSettingsPayload = {}
      const [startDate, endDate] = this.planningDateRange

      // 2. Temporäre Einstellungen sammeln (unverändert)
      try {
        const readyEmployeesData = this.confirmedEmployeeSettings.filter((emp) => this.confirmationDialogData.readyEmployeeIds.includes(emp.user))
        readyEmployeesData.forEach((employee) => {
          const settingsKey = `planningSettings_${employee._id}_${this.selectedStudio}`
          const savedSettings = JSON.parse(localStorage.getItem(settingsKey)) || {}
          planningSettingsPayload[employee._id.toString()] = {
            percentages: savedSettings.percentages || {},
            recurringAbsence: savedSettings.recurringAbsence || [],
          }
        })
      } catch (e) {
        console.error("Fehler beim Sammeln der planningSettings:", e)
        this.$toast.error("Fehler beim Lesen der Mitarbeiter-Einstellungen.")
        this.assigning = false
        return
      }

      console.log("Schritt 4a: Generiere Schichten aus Vorlage...")

      try {
        // *** 3. NEUER SCHRITT: Schichten generieren ***
        const generationResponse = await api.shiftManagement.generatePlanFromTemplate(this.selectedStudio, startDate, endDate)
        console.log(`[GENERATE_FRONTEND] ${generationResponse.createdCount} Schichten erstellt.`)
        this.$toast.info(`${generationResponse.createdCount} Schichten wurden aus der Vorlage erstellt...`)

        // 4. Backend-API für Zuweisung aufrufen (wie zuvor)
        console.log("Schritt 4b: Weise Mitarbeiter zu...")
        const updatedPlan = await api.shiftManagement.assignEmployeesToPlan(
          this.selectedStudio,
          startDate,
          endDate,
          planningSettingsPayload,
          this.confirmationDialogData.readyEmployeeIds
        )

        // 5. Ergebnis verarbeiten
        if (!updatedPlan || updatedPlan.length === 0) {
          // Dies passiert, wenn der Zuweisungs-Algorithmus fehlschlägt
          console.error("Zuweisung fehlgeschlagen, Backend gab leeren Plan zurück.")
          this.$toast.error("Zuweisung fehlgeschlagen. (Keine Zuweisungsdaten)")
          this.handleAssignmentComplete([]) // Leeren Plan übergeben
        } else {
          this.handleAssignmentComplete(updatedPlan) // Zum Roster (Schritt 5)
        }
      } catch (error) {
        // Fängt Fehler von *beiden* API-Aufrufen (generate und assign)
        this.$toast.error("Fehler im Zuweisungsprozess.")
        console.error("Assignment Process Error:", error.response ? error.response.data : error)
        this.assigning = false
      }
    },

    // *** KORRIGIERT: Greift jetzt auf confirmedEmployeeSettings zu (oder lädt neu) ***
    async handleAssignmentComplete(updatedPlan) {
      this.assignedPlanData = updatedPlan.filter((p) => p.assignedEmployee) // Daten für Roster
      this.assigning = true // Ladezustand für Mitarbeiteraufbereitung
      try {
        if (!this.confirmedEmployeeSettings || this.confirmedEmployeeSettings.length === 0) {
          console.error("Mitarbeiterdaten aus Schritt 3 nicht gefunden!")
          // Optionally re-load them from the ref as a fallback
          // this.confirmedEmployeeSettings = this.$refs.employeeSettings?.employees || [];
        }
        // Lade ALLE Mitarbeiterdaten erneut, um sicherzustellen, dass wir die richtigen haben
        // (Sicherer als sich auf confirmedEmployeeSettings zu verlassen, falls die Seite neu geladen wurde)
        const allStudioEmployees = await api.shiftManagement.fetchShiftManagementEmployee()
        const relevantEmployees = allStudioEmployees.filter((emp) => emp.studio && emp.studio.some((s) => s._id === this.selectedStudio))
        const employeeMap = new Map(this.confirmedEmployeeSettings.map((e) => [e.user, e]))

        const assignedEmployeeIds = [...new Set(updatedPlan.filter((p) => p.assignedEmployee).map((p) => p.assignedEmployee._id))]

        this.assignedEmployees = assignedEmployeeIds
          .map((id) => {
            const fullEmployeeData = employeeMap.get(id)
            if (!fullEmployeeData) return null

            const savedSettings = JSON.parse(localStorage.getItem(`planningSettings_${fullEmployeeData._id}_${this.selectedStudio}`)) || {}
            const planningSettings = {
              percentages: savedSettings.percentages || {},
              recurringAbsence: savedSettings.recurringAbsence || [],
              isReadyForPlanning: savedSettings.isReadyForPlanning || false,
            }

            return {
              ...fullEmployeeData, // Passes vacations, schoolDays, etc.
              _id: id, // Ensure user ID is correct
              sollStunden: this.calculateSollStunden(fullEmployeeData, planningSettings),
              plannedHours: this.calculatePlannedHours(id, updatedPlan) + this.calculatePaidVacationHours(fullEmployeeData, planningSettings), //
              planningSettings: planningSettings, // Pass the temporary settings
            }
          })
          .filter((e) => e !== null)
      } catch (error) {
        console.error("Fehler beim Laden/Verarbeiten der Mitarbeiterdaten für Roster:", error)
        this.$toast.error("Fehler beim Aufbereiten der Mitarbeiterdaten.")
        this.assignedEmployees = []
      } finally {
        this.assigning = false
        this.step = 5 // Wechsle zu Schritt 5 (Roster)
      }
    },
    // ERSETZE die existierende Funktion in 'test/shiftManagementMainDialogCreate.vue'
    calculateSollStunden(employeeData, planningSettings) {
      // FIX: Verwende planningDateRange statt planningStartDate/EndDate
      if (!this.planningDateRange || this.planningDateRange.length < 2 || !employeeData) return 0
      const start = this.$moment(this.planningDateRange[0])
      const end = this.$moment(this.planningDateRange[1])
      // --- ENDE FIX ---

      let businessDays = 0
      let current = start.clone()

      // FIX: Verwende selectedStudio statt studioId
      const studioInfo = employeeData.studio.find((s) => s._id === this.selectedStudio)
      if (!studioInfo) return 0
      // --- ENDE FIX ---

      // FIX: Übergebe planningSettings an die neue getPlanningPercentage Funktion
      const percentage = this.getPlanningPercentage(employeeData, studioInfo, planningSettings)
      // --- ENDE FIX ---

      // console.log(`Berechne Sollstunden für ${employeeData.fullName} mit ${percentage}%`)

      while (current.isSameOrBefore(end, "day")) {
        const dayOfWeekISO = current.isoWeekday()
        const isWeekend = dayOfWeekISO >= 6

        // FIX: Verwende this.settings.schoolHolidays (aus dem Parent-State)
        const isSchoolHoliday = (this.settings.schoolHolidays || []).some(
          (sh) => sh.startDate && sh.endDate && current.isBetween(sh.startDate, sh.endDate, null, "[]")
        )
        // --- ENDE FIX ---

        let isSchoolDay = false
        if (employeeData.isApprentice && !isSchoolHoliday) {
          isSchoolDay = (employeeData.schoolDays || []).some((sd) => sd.dayOfWeek === dayOfWeekISO)
        }

        // Logik kopiert von shiftManagementMainDialogCreateEmployee.vue
        let isWorkDay = !isWeekend && !isSchoolDay /* && !isHoliday */

        // Zusätzliche Prüfung für Lehrlinge während Schulferien
        if (employeeData.isApprentice && isSchoolHoliday) {
          isWorkDay = false // Lehrlinge arbeiten nicht während Schulferien
        }

        if (isWorkDay) {
          businessDays++
        }
        current.add(1, "day")
      }

      // Korrekte Berechnung der Sollstunden basierend auf den effektiven Arbeitstagen
      return businessDays * (8.4 * (percentage / 100))
    },

    // Berechnet die Summe der geplanten Stunden für einen Mitarbeiter
    calculatePlannedHours(employeeId, planData) {
      let totalHours = 0
      planData.forEach((plan) => {
        if (plan.assignedEmployee && plan.assignedEmployee._id === employeeId && plan.shiftTemplate) {
          plan.shiftTemplate.times.forEach((time) => {
            if (time.type === "Arbeitszeit" && time.start && time.end) {
              const startTime = this.$moment(time.start, "HH:mm")
              const endTime = this.$moment(time.end, "HH:mm")
              if (endTime.isBefore(startTime)) {
                endTime.add(1, "day")
              }
              totalHours += endTime.diff(startTime, "hours", true)
            }
          })
        }
      })
      return totalHours
    },

    // *** KORRIGIERT: Speichert die Mitarbeiterdaten, bevor der Schritt gewechselt wird ***
    nextStep() {
      // Prüfe beim Verlassen von Schritt 3
      if (this.step === 3) {
        if (this.$refs.employeeSettings) {
          const status = this.$refs.employeeSettings.getConfirmationStatus()
          this.confirmationDialogData.notReadyNames = status.notReadyNames
          this.confirmationDialogData.readyEmployeeIds = status.readyEmployeeIds

          // *** NEU: Speichere die relevanten Mitarbeiterdaten ***
          this.confirmedEmployeeSettings = this.$refs.employeeSettings.employees || []

          if (!status.allMarked) {
            this.showConfirmationDialog = true
            return // Verhindere den direkten Schrittwechsel
          }
        } else {
          console.error("Referenz zu employeeSettings nicht gefunden in nextStep")
          this.confirmationDialogData.readyEmployeeIds = []
          this.confirmedEmployeeSettings = [] // Leeren
        }
      }

      // Gehe zum nächsten Schritt
      if (this.step < 5) {
        this.step++
      }
    },
    prevStep() {
      if (this.step > 1) this.step--
    },
    // Wird vom Bestätigungsdialog aufgerufen
    proceedToAssignment() {
      this.showConfirmationDialog = false // Dialog schliessen
      // Die 'readyEmployeeIds' UND 'confirmedEmployeeSettings' sind bereits gespeichert
      this.step = 4 // Manuell zu Schritt 4 (Zuweisung) wechseln
    },
    // Helfer für Auto-Select, wenn nur 1 Studio
    autoSelectStudio() {
      if (this.availableStudios.length === 1 && !this.selectedStudio) {
        this.$nextTick(() => {
          this.selectedStudio = this.availableStudios[0]._id
          // Löst den Watcher aus, der fetchWeeklyTemplate aufruft
        })
      }
      return ""
    },
  },
  watch: {
    selectedStudio(newStudio, oldStudio) {
      if (this.initialLoadComplete && newStudio !== oldStudio) {
        console.log("Watcher: selectedStudio changed, fetching template...")
        this.fetchWeeklyTemplate()
      }
    },
    step(newStep, oldStep) {
      if (this.initialLoadComplete && newStep === 1 && oldStep > 1) {
        console.log("Watcher: step changed back to 1, refetching template...")
        this.fetchWeeklyTemplate()
      }
      if (newStep === 3 && this.loadedWeeklyTemplate.length === 0 && !this.loadingTemplate) {
        console.log("Watcher: step changed to 3, fetching template for props...")
        this.fetchWeeklyTemplate()
      }
    },
  },
}
</script>

<style scoped>
::v-deep .v-calendar-daily_head-day-label span {
  word-break: keep-all !important;
}
.draggable-shift {
  cursor: move;
  user-select: none;
}
.calendar-drop-zone {
  min-height: 500px;
}
.frozen-col {
  position: sticky;
  left: 0;
  background: #1e1e1e;
  z-index: 2;
  border-right: 1px solid rgba(255, 255, 255, 0.12);
}
.frozen-col-summary {
  position: sticky;
  right: 0;
  background: #1e1e1e;
  z-index: 2;
  border-left: 1px solid rgba(255, 255, 255, 0.12);
}
::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}
</style>
